import { FirmwareUpdateStage, FirmwareUpdateStatus, PropertyPanelDevice } from '@shure/cloud/shared/models/devices';
import { mapDurationFromSysApi } from '@shure/shared/angular/data-access/system-api/mappers';

import { DeviceStatus, PropertyPanelDeviceFragment } from '../graphql/generated/cloud-sys-api';

import { mapBatteryStateFromSysApi } from './map-battery-state';
import { mapDeviceAuthenticationFromSysApi } from './map-device-authentication';
import { mapExtendedWarrantyFromSysApi } from './map-extended-warranty';
import { mapFirmwarePackagesFromSysApi } from './map-firmware-packages';
import { mapLicenseInfoFromSysApi } from './map-license-info';
import { mapLicenseInfoV2FromSysApi } from './map-license-info-v2';
import { mapLicenseV3FromSysApi } from './map-license-v3';
import { mapPairedDevicesFromSysApi } from './map-paired-devices';
import { mapScheduledJobRequestFromSysApi } from './map-scheduled-job-request';
import {
	deviceFragmentToMicStateFeature,
	mapCompatibilityLevelFromSysApi,
	mapDeviceModelFromSysApi,
	mapExternalDisplaysFromSysApi,
	mapFeatureValueFromSysApi,
	mapHostedFirmwareVersionFromSysApi,
	mapIpModeFromSysApi,
	mapRfChannelsFromSysApi,
	mapUptimeDurationFromSysApi
} from './utils';

export function mapPropertyPanelDeviceFromSysApi(deviceSysApi: PropertyPanelDeviceFragment): PropertyPanelDevice {
	const {
		id,
		hardwareId,
		virtual,
		compatibility,
		description: { features: descriptionFeatures, constraints: constraintFeatures },
		interface: { model: deviceModel },
		features: {
			audioChannelCount,
			audioMute,
			audioNetwork,
			authentication,
			availablePackages,
			batteryLevel,
			controlNetwork,
			danteAudioNetwork,
			extendedWarranty,
			externalDisplays,
			firmware,
			hostedFirmware,
			hostedUpdateProgress,
			identify,
			iotRoomProxy,
			license,
			licenseV2,
			licenseV3,
			name,
			pairedDevices,
			rfChannels,
			scheduledJobs,
			serialNumber,
			tags,
			updateProgress,
			uptime
		}
	} = deviceSysApi;

	const pendingFirmwareJobRequest = scheduledJobs?.firmwareJobRequests.pending;

	const device: PropertyPanelDevice = {
		id,
		hardwareId,
		name: mapFeatureValueFromSysApi(name?.name, ''),
		model: mapDeviceModelFromSysApi(deviceModel ?? ''),
		status: deviceSysApi.status,
		isVirtual: virtual,
		compatibility: mapCompatibilityLevelFromSysApi(compatibility),
		manufacturer: deviceSysApi.manufacturer,
		constraints: {
			name: {
				regex: constraintFeatures?.name?.name.pattern ?? ''
			}
		},

		features: {
			audioChannelCount: {
				count: audioChannelCount?.count ?? 0,
				isMissing: !descriptionFeatures.audioChannelCount?.supported
			},
			mute: {
				muted: audioMute?.muted || false,
				isMissing: !descriptionFeatures.audioMute.supported
			},
			audioNetwork: {
				gateway: mapFeatureValueFromSysApi(audioNetwork?.interface.gateway),
				ipAddress: mapFeatureValueFromSysApi(audioNetwork?.interface.ipAddress),
				ipMode: mapIpModeFromSysApi(audioNetwork?.interface.ipMode),
				subnetMask: mapFeatureValueFromSysApi(audioNetwork?.interface.subnetMask),
				macAddress: mapFeatureValueFromSysApi(audioNetwork?.interface.macAddress),
				isMissing: !descriptionFeatures.audioNetwork.supported
			},
			authentication: {
				...mapDeviceAuthenticationFromSysApi(authentication),
				isMissing: !descriptionFeatures.authentication?.supported
			},
			availablePackages: {
				...mapFirmwarePackagesFromSysApi(availablePackages),
				isMissing: !descriptionFeatures.availablePackages.supported
			},
			batteryLevel: {
				percentage: batteryLevel?.percentage ?? 0,
				currentState: mapBatteryStateFromSysApi(batteryLevel?.status.currentState),
				targetState: mapBatteryStateFromSysApi(batteryLevel?.status.targetState),
				timeToTargetState: mapDurationFromSysApi(batteryLevel?.status.timeToTargetState),
				isMissing: !descriptionFeatures.batteryLevel?.supported
			},
			controlNetwork: {
				ipMode: mapIpModeFromSysApi(controlNetwork?.interface.ipMode),
				ipAddress:
					deviceSysApi.status === DeviceStatus.Error
						? mapFeatureValueFromSysApi(deviceSysApi.protocol.dmp?.ipAddress)
						: mapFeatureValueFromSysApi(controlNetwork?.interface.ipAddress),
				subnetMask: mapFeatureValueFromSysApi(controlNetwork?.interface.subnetMask),
				gateway: mapFeatureValueFromSysApi(controlNetwork?.interface.gateway),
				macAddress: mapFeatureValueFromSysApi(controlNetwork?.interface.macAddress),
				isMissing: !descriptionFeatures.controlNetwork.supported
			},
			danteName: {
				danteName: mapFeatureValueFromSysApi(danteAudioNetwork?.name),
				isMissing: !descriptionFeatures.danteAudioNetwork.supported
			},
			extendedWarranty: descriptionFeatures.extendedWarranty?.supported
				? {
						...mapExtendedWarrantyFromSysApi(extendedWarranty),
						isMissing: !extendedWarranty
				  }
				: undefined,
			externalDisplays: {
				...mapExternalDisplaysFromSysApi(externalDisplays?.displays ?? []),
				isMissing: !descriptionFeatures.externalDisplays?.supported
			},
			firmware: {
				version: mapFeatureValueFromSysApi(firmware?.version),
				valid: firmware?.valid ?? true,
				isMissing: !descriptionFeatures.firmware.supported
			},
			hostedFirmware: {
				version: mapHostedFirmwareVersionFromSysApi(hostedFirmware?.version),
				key: mapFeatureValueFromSysApi(hostedFirmware?.key, ''),
				isMissing: !descriptionFeatures.hostedFirmware?.supported
			},
			hostedUpdateProgress: {
				package: {
					key: hostedUpdateProgress?.firmwarePackageKey ?? '',
					version: hostedUpdateProgress?.firmwarePackageVersion ?? '0.0.0.0'
				},
				percentComplete: hostedUpdateProgress?.stageProgressPercentage ?? 100,
				stage: hostedUpdateProgress?.updateStage ?? FirmwareUpdateStage.Idle,
				status: hostedUpdateProgress?.updateStatus ?? FirmwareUpdateStatus.Success,
				isMissing: !descriptionFeatures.hostedUpdateProgress?.supported
			},
			identify: {
				identifying: identify?.identifying || false,
				isMissing: !descriptionFeatures.identify.supported
			},
			iotRoomProxy: descriptionFeatures.iotRoomProxy?.supported
				? {
						enabled: iotRoomProxy?.enabled ?? false,
						isMissing: !iotRoomProxy
				  }
				: undefined,
			license: descriptionFeatures.license?.supported
				? {
						info: mapLicenseInfoFromSysApi(license?.info ?? []),
						isMissing: !license
				  }
				: undefined,

			licenseV2: descriptionFeatures.licenseV2?.supported
				? {
						info: mapLicenseInfoV2FromSysApi(licenseV2?.info ?? []),
						isMissing: !licenseV2
				  }
				: undefined,

			licenseV3: descriptionFeatures.licenseV3?.supported
				? {
						license: mapLicenseV3FromSysApi(licenseV3),
						isMissing: !licenseV3
				  }
				: undefined,

			micStatus: deviceFragmentToMicStateFeature(deviceSysApi),

			name: {
				name: name?.name,
				isMissing: !descriptionFeatures.name?.supported
			},
			pairedDevices: descriptionFeatures.pairedDevices?.supported
				? {
						devices: pairedDevices?.pairedDevices
							? mapPairedDevicesFromSysApi(pairedDevices.pairedDevices)
							: [],
						isMissing: !pairedDevices
				  }
				: undefined,
			// reboot only has a feature description. No feature value
			reboot: {
				supported: descriptionFeatures.reboot?.supported ?? false
			},
			rfChannels: descriptionFeatures.rfChannels?.supported
				? {
						channels: mapRfChannelsFromSysApi(rfChannels?.rfChannels ?? []),
						isMissing: !rfChannels
				  }
				: undefined,
			scheduledJobs: descriptionFeatures.scheduledJobs?.supported
				? {
						scheduledJobRequests: pendingFirmwareJobRequest
							? [mapScheduledJobRequestFromSysApi(pendingFirmwareJobRequest)]
							: [],
						isMissing: !scheduledJobs
				  }
				: undefined,
			serialNumber: {
				serialNumber: mapFeatureValueFromSysApi(serialNumber?.serialNumber),
				isMissing: !descriptionFeatures.serialNumber.supported
			},
			updateProgress: {
				stage: updateProgress?.updateStage ?? FirmwareUpdateStage.Idle,
				status: updateProgress?.updateStatus ?? FirmwareUpdateStatus.Success,
				isMissing: !descriptionFeatures.updateProgress.supported
			},
			uptime: {
				uptime: mapUptimeDurationFromSysApi(uptime?.uptime),
				isMissing: !descriptionFeatures.uptime.supported
			},
			tags: {
				tags: tags?.tags ?? [],
				isMissing: !descriptionFeatures.tags?.supported
			}
		}
	};

	return device;
}
